import { useEffect } from "react";
import { PencilIcon } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";

import {
  Column,
  ErrorText,
  LoadingIndicator,
  Text,
  TopbarContainer,
  TopbarTitle,
} from "@/components";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { useSetTitle } from "@/hooks/useSetTitle";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS, MIXPANEL_SUBSCREENS } from "@/lib/mixpanel/types";
import { novaClient } from "@/lib/trpc";
import { DemoConfig } from "@/types";

export const ToolsDemoConfigsShowPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  if (!id) {
    throw new Error("No ID found in URL for demo config show page!");
  }

  const { data: demoConfig, status } =
    novaClient.internal.admin.demoConfigs.findOne.useQuery({
      id,
    });

  useSetTitle({
    title: "Tools | Demo Configs | Show",
  });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.DEMO_CONFIGS, {
      subScreenName: MIXPANEL_SUBSCREENS.SHOW,
    });
  }, []);

  return (
    <Column className="h-svh">
      <TopbarContainer>
        <TopbarTitle className="!text-slate-500">
          <Link className="hover:underline" to="/tools/demo-configs">
            Demo Configs
          </Link>{" "}
          / <span className="text-slate-700">{id}</span>
        </TopbarTitle>
        <Button
          className="rounded-full"
          onClick={() => {
            void navigate(`/tools/demo-configs/${id}/edit`);
          }}
        >
          <PencilIcon className="h-4 w-4" />
          Edit
        </Button>
      </TopbarContainer>
      <div className="flex-1 overflow-y-auto p-6">
        {status === "pending" && <LoadingIndicator variant="medium" />}
        {status === "error" && (
          <ErrorText errorMessage="Error loading demo config." />
        )}
        {status === "success" && (
          <Column className="gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-700">
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <div>
                  <Text variant="small">Title</Text>
                  <p className="text-slate-700">{demoConfig.title}</p>
                </div>
                <div>
                  <Text variant="small">Region</Text>
                  {/* TODO: Improve this. */}
                  <p className="text-slate-700">
                    {(demoConfig as DemoConfig).config.region}
                  </p>
                </div>
                <div>
                  <Text variant="small">Position</Text>
                  {/* TODO: Improve this. */}
                  <p className="text-slate-700">
                    {(demoConfig as DemoConfig).config.position}
                  </p>
                </div>
                <div>
                  <Text variant="small">Version</Text>
                  <p className="text-slate-700">{demoConfig.version}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-700">
                  Stevie Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <div>
                  <Text variant="small">Client</Text>
                  <p className="text-slate-700">
                    {/* TODO: Improve this. */}
                    {(demoConfig as DemoConfig).config.stevie.client}
                  </p>
                </div>
                <div>
                  <Text variant="small">Job Title</Text>
                  <p className="text-slate-700">
                    {/* TODO: Improve this. */}
                    {(demoConfig as DemoConfig).config.stevie.jobTitle}
                  </p>
                </div>
                <div>
                  <Text variant="small">Job Description</Text>
                  <p className="whitespace-pre-wrap text-slate-700">
                    {/* TODO: Improve this. */}
                    {(demoConfig as DemoConfig).config.stevie.jobDescription}
                  </p>
                </div>
                <div>
                  <Text variant="small">Open-Ended Question</Text>
                  <p className="text-slate-700">
                    {/* TODO: Improve this. */}
                    {(demoConfig as DemoConfig).config.stevie.openEndedQuestion}
                  </p>
                </div>
                <div>
                  <Text variant="small">Filter Question Sections</Text>
                  <Column className="gap-4">
                    {/* TODO: Improve this. */}
                    {(
                      demoConfig as DemoConfig
                    ).config.stevie.filterQuestionSections.map(
                      (section, index) => (
                        <div
                          key={index}
                          className="rounded-lg border border-slate-200 bg-slate-50 p-4"
                        >
                          <Text variant="small">{section.title}</Text>
                          <ul className="list-disc pl-6">
                            {section.questions.map((question, qIndex) => (
                              <li key={qIndex} className="text-slate-700">
                                {question}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ),
                    )}
                  </Column>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-700">
                  Hide Candidates for Companies
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* TODO: Improve this. */}
                {(demoConfig as DemoConfig).config.hideCandidatesForCompanies
                  .length === 0 ? (
                  <p className="text-slate-700">None</p>
                ) : (
                  <ul className="list-disc pl-6">
                    {/* TODO: Improve this. */}
                    {(
                      demoConfig as DemoConfig
                    ).config.hideCandidatesForCompanies.map(
                      (company, index) => (
                        <li key={index} className="text-slate-700">
                          {company}
                        </li>
                      ),
                    )}
                  </ul>
                )}
              </CardContent>
            </Card>
          </Column>
        )}
      </div>
    </Column>
  );
};
